import React, { useState, useEffect, useCallback, useContext, createContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import OfferPagePromo from '../OfferPagePromo';
import CheckContent from '../../../CheckContent';
import OfferPageGoalsComponent from '../OfferPageGoals/OfferPageGoals.container';
import LinkConstructorComponent from '../../../constructors/Link/LinkConstructor.container';
import PublisherActions from '../../../../actions/PublisherActions';
import PromoCodeConstructorComponent from "../../../constructors/PromoCode/PromoCodeConstructor.container";
import PromoCodeListConstructorComponent from "../../../constructors/PromoCodeList/PromoCodeListConstructor.container";
import { I18nContext } from 'utils/I18n/context';

const OfferContext = createContext();

const OfferPageContainer = ({ 
    params,
    offer: propOffer,
    isEmpty: propIsEmpty,
    isPaused: propIsPaused,
    loading: propLoading,
    location,
    searchGoalEntries,
    searchLinks,
    searchBanners,
    searchContainers,
    searchLandings,
    saveLinkPresets,
    savePromoCode,
    editPromoCode,
    deletePromoCode
}) => {
    const { t } = useContext(I18nContext);
    const [offer, setOffer] = useState(propOffer || {});
    const [isEmpty, setIsEmpty] = useState(propIsEmpty);
    const [isPaused, setIsPaused] = useState(propIsPaused);
    const [loading, setLoading] = useState(propLoading);
    const [constructorLinkIsVisible, setConstructorLinkIsVisible] = useState(false);
    const [constructorPromocodeIsVisible, setConstructorPromocodeIsVisible] = useState(false);
    const [constructorPromocodeListIsVisible, setConstructorPromocodeListIsVisible] = useState(false);
    const [linkId, setLinkId] = useState(null);
    const [creativeType, setCreativeType] = useState(null);
    const [creativeId, setCreativeId] = useState(null);
    const [goalEntryId, setGoalEntryId] = useState(null);
    const [type, setType] = useState('link');
    const [codeParams, setCodeParams] = useState(null);
    const [access, setAccess] = useState(true);
    const [promocode, setPromocode] = useState(null);
    const [link, setLink] = useState(null);

    useEffect(() => {
        const defaultLink = propOffer?.default_link || {};
        setOffer(propOffer || {});
        setLinkId(linkId || defaultLink.id);
        setLoading(propLoading);
        setIsEmpty(propIsEmpty);
        setIsPaused(propIsPaused);
    }, [propOffer, propLoading, propIsEmpty, propIsPaused, linkId]);

    useEffect(() => {
        PublisherActions.getOffers({ids: params.id});
        searchGoalEntries(params.id);
        searchLinks({offer_ids: Number(params.id)});
        searchBanners({offer_ids: Number(params.id)});
        searchContainers({offer_ids: Number(params.id)});
        searchLandings({offer_ids: Number(params.id)});
    }, [params.id, searchGoalEntries, searchLinks, searchBanners, searchContainers, searchLandings]);

    const getClassTabItem = useCallback((key) => {
        const urlParams = new URLSearchParams(location?.search);
        const tab = urlParams.get('tab') || 'goals';
        return classNames({
            'active': tab === key
        });
    }, [location]);

    const getClassTabPane = useCallback((key) => {
        const urlParams = new URLSearchParams(location?.search);
        const tab = urlParams.get('tab') || 'goals';
        return classNames({
            'tab-pane': true,
            'active': tab === key
        });
    }, [location]);

    const showConstructor = useCallback((params = {}) => {
        setCreativeType(params.creativeType);
        setCreativeId(params.creativeId);
        setConstructorLinkIsVisible(true);
        setGoalEntryId(params.goalEntryId);
        setType(params.type || 'link');
        setCodeParams(params.codeParams);
        setLinkId(params.linkId);
        setAccess(!(params.access === false));
    }, []);

    const showConstructorPromoCode = useCallback((params = {}) => {
        setCreativeType(params.creativeType);
        setCreativeId(params.creativeId);
        setConstructorPromocodeIsVisible(true);
        setGoalEntryId(params.goalEntryId);
        setType(params.type || 'promocode');
        setCodeParams(params.codeParams);
        setLinkId(params.linkId);
        setPromocode(params.promocode);
        setAccess(!(params.access === false));
    }, []);

    const showConstructorPromoCodeList = useCallback((params = {}) => {
        setCreativeType(params.creativeType);
        setCreativeId(params.creativeId);
        setConstructorPromocodeListIsVisible(true);
        setGoalEntryId(params.goalEntryId);
        setType(params.type || 'promocodeList');
        setCodeParams(params.codeParams);
        setLinkId(params.linkId);
        setLink(params.link);
        setAccess(!(params.access === false));
    }, []);

    const onCloseLinkConstructor = useCallback(() => {
        setConstructorLinkIsVisible(false);
    }, []);

    const onClosePromocodeConstructor = useCallback(() => {
        setConstructorPromocodeIsVisible(false);
    }, []);

    const onClosePromocodeListConstructor = useCallback(() => {
        setConstructorPromocodeListIsVisible(false);
    }, []);

    const contextValue = {
        params,
        showConstructor,
        showConstructorPromoCode,
        showConstructorPromoCodeList,
    };

    const staticNames = t?.offersPage?.offer || {};

    return (
        <OfferContext.Provider value={contextValue}>
            <CheckContent
                isEmpty={isEmpty}
                isPaused={isPaused}
                loading={loading}
            >
                <div className="ibox offer-item">
                    <div className="ibox-content">
                        <ul className="nav nav-tabs" id="myTab">
                            <li className={getClassTabItem('goals')}>
                                <Link to={`${location.pathname}?tab=goals`}>
                                    {staticNames.goalsTitle || 'Goals'}
                                </Link>
                            </li>
                            <li className={getClassTabItem('promo')}>
                                <Link to={`${location.pathname}?tab=promo`}>
                                    {staticNames.promoTitle || 'Promo'}
                                </Link>
                            </li>
                        </ul>
                        <div className="tab-content">
                            <div className={getClassTabPane('goals')}>
                                <OfferPageGoalsComponent />
                            </div>
                            <div className={getClassTabPane('promo')}>
                                <OfferPagePromo
                                    location={location}
                                    showConstructor={showConstructor}
                                    showConstructorPromoCode={showConstructorPromoCode}
                                    showConstructorPromoCodeList={showConstructorPromoCodeList}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </CheckContent>

            {!loading && (offer.title || offer.name) && (
                <LinkConstructorComponent
                    creativeType={creativeType}
                    creativeId={creativeId}
                    offer={offer}
                    goalEntryId={goalEntryId}
                    linkId={linkId}
                    onClose={onCloseLinkConstructor}
                    visible={constructorLinkIsVisible}
                    access={access}
                    type={type}
                    codeParams={codeParams}
                    onSave={saveLinkPresets}
                />
            )}

            {!loading && (offer.title || offer.name) && (
                <PromoCodeConstructorComponent
                    creativeType="promocode"
                    creativeId={creativeId}
                    offer={offer}
                    goalEntryId={goalEntryId}
                    linkId={linkId}
                    promocode={promocode}
                    onClose={onClosePromocodeConstructor}
                    visible={constructorPromocodeIsVisible}
                    access={access}
                    type={type}
                    codeParams={codeParams}
                    onSave={savePromoCode}
                    onEdit={editPromoCode}
                />
            )}

            {!loading && (offer.title || offer.name) && (
                <PromoCodeListConstructorComponent
                    creativeType="link"
                    creativeId={creativeId}
                    offer={offer}
                    goalEntryId={goalEntryId}
                    link={link}
                    onClose={onClosePromocodeListConstructor}
                    visible={constructorPromocodeListIsVisible}
                    access={access}
                    type={type}
                    codeParams={codeParams}
                    onDelete={deletePromoCode}
                    onEdit={editPromoCode}
                />
            )}
        </OfferContext.Provider>
    );
};

OfferPageContainer.propTypes = {
    params: PropTypes.object.isRequired,
    offer: PropTypes.object,
    isEmpty: PropTypes.bool,
    isPaused: PropTypes.bool,
    loading: PropTypes.bool,
    location: PropTypes.object,
    searchGoalEntries: PropTypes.func.isRequired,
    searchLinks: PropTypes.func.isRequired,
    searchBanners: PropTypes.func.isRequired,
    searchContainers: PropTypes.func.isRequired,
    searchLandings: PropTypes.func.isRequired,
    saveLinkPresets: PropTypes.func.isRequired,
    savePromoCode: PropTypes.func.isRequired,
    editPromoCode: PropTypes.func.isRequired,
    deletePromoCode: PropTypes.func.isRequired
};

OfferPageContainer.defaultProps = {
    offer: {},
    isEmpty: false,
    isPaused: false,
    loading: false,
    location: {}
};

// Export context for child components to use
export { OfferContext };
export default OfferPageContainer;